import * as React from 'react';
import { Async } from 'office-ui-fabric-react/lib/Utilities';
import { IDownloadData, IDownloaderProps } from '../../controls/downloadItemsActionControl';
import { PlatformDetection } from '@ms/utilities-browser/lib/PlatformDetection';
import { navigateViaPost } from '@ms/items-view-utilities/lib/navigation/NavigateViaPost';

/**
 * @internal
 */
export interface IDownloaderState {
  downloadData?: IDownloadData;
  targetDocument?: Document;
}

const platformDetection = new PlatformDetection();

// IE enforces security boundaries around usage of iframe elements for downloads.
// As a workaround, downloads must be initiated in a new tab.
const useNewTabForDownload = platformDetection.isIE;

const DOWNLOAD_START_GRACE_PERIOD = 20 * 1000;

const INITIAL_STATE: IDownloaderState = {};

/**
 * @internal
 */
export class Downloader extends React.PureComponent<IDownloaderProps, IDownloaderState> {
  private _async: Async;
  private _window: Window | null;

  constructor(props: IDownloaderProps) {
    super(props);

    this._async = new Async(this);
    this._window = null;
    this.state = INITIAL_STATE;
  }

  public render(): JSX.Element | null {
    const {
      downloadData
    } = this.state;

    if (!downloadData) {
      return null;
    }

    if (!useNewTabForDownload && !this.props.preferOpenInBrowser) {
      return (
        <div style={ { display: 'none' } }>
          <iframe
            hidden={ true }
            ref={ this._onFrameRef }
            src={ downloadData.method === 'GET' ? downloadData.url : 'about:blank' }
          />
        </div>
      );
    }

    return null;
  }

  public async componentWillMount(): Promise<void> {
    this._getDownloadData();
  }

  public componentDidMount(): void {
    if (useNewTabForDownload || this.props.preferOpenInBrowser) {
      try {
        // In IE, the download must go to a new window, but since the download URL
        // will be determined asynchronously the window needs to be created immediately with an empty URL.
        this._window = window.open();
      } catch (e) {
        // Cannot open window for download.
      }
    }

    this._onUpdate(INITIAL_STATE);

    if (this._window) {
      const targetDocument = this._window && this._window.document;

      this.setState({
        targetDocument: targetDocument || undefined
      });
    }
  }

  public componentDidUpdate(previousProps: IDownloaderProps, previousState: IDownloaderState): void {
    this._onUpdate(previousState);
  }

  public componentWillUnmount(): void {
    if (this._window) {
      this._window.close();
      this._window = null;
    }
  }

  public componentDidCatch(error: Error): void {
    this.props.reject(error);
  }

  private _onFrameRef = (formElement: HTMLIFrameElement | null): void => {
    const contentDocument = formElement && formElement.contentDocument;

    this.setState({
      targetDocument: contentDocument || undefined
    });
  }

  private _onUpdate(previousState: IDownloaderState): void {
    const {
      downloadData,
      targetDocument
    } = this.state;

    if (!previousState.downloadData && downloadData) {
      this._async.setTimeout(this._onDownloadTimeout, DOWNLOAD_START_GRACE_PERIOD);
    }

    try {
      if (!previousState.downloadData && downloadData &&
        downloadData.method === 'GET' && this._window) {
        this._window.location.href = downloadData.url;
      }

      if (!(previousState.targetDocument && previousState.downloadData) && (targetDocument && downloadData) &&
        downloadData.method === 'POST') {
        navigateViaPost({
          document: targetDocument,
          postdata: downloadData.postData,
          url: downloadData.url
        });
      }
    } catch (e) {
      this.props.reject(e);
    }
  }

  private async _getDownloadData(): Promise<void> {
    try {
      const downloadData = await this.props.getDownloadData();

      this.setState({
        downloadData: downloadData
      });
    } catch (e) {
      this.props.reject(e);
    }
  }

  private _onDownloadTimeout(): void {
    this.props.resolve();
  }
}
